/**
 * `/team` - operator-team management (OCP-168). Lives in the
 * Operations sidebar group. OCP-755: roles super_admin / operator /
 * triage and the second factor per account; a super_admin changes roles
 * and resets a lost factor (each with an audited reason), never its own.
 *
 * Self-protection: server-side guards refuse to deactivate the caller
 * or the last active super_admin. The SPA mirrors both with greyed-
 * out buttons + tooltip so the operator doesn't click into a 4xx.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { fmtDate } from '../lib/format-time';

interface TeamRow {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  pending_setup: boolean;
  active_sessions: number;
  /** OCP-699 - human or bot; a bot has grants instead of sessions. */
  kind: 'human' | 'bot';
  display_name: string | null;
  purpose: string | null;
  active_grants: number;
  /** OCP-755 - humans: a verified TOTP second factor. */
  mfa_enabled?: boolean;
}

const ROLE_HELP: Record<string, string> = {
  super_admin: 'everything, incl. team, roles, bot grants, security settings',
  operator: 'every operator action except account administration and security settings',
  triage: 'reads plus the alert-triage writes (ack / resolve / escalate, incident voids, eval cases)',
};

interface BotGrant {
  id: string;
  client_id: string;
  client_name: string | null;
  label: string;
  reason: string;
  created_by: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revoke_reason: string | null;
  last_used_at: string | null;
  status: 'active' | 'revoked' | 'expired';
}

interface OAuthClientRow {
  client_id: string;
  client_name: string;
}

export function AdminTeamPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<TeamRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [creatingBot, setCreatingBot] = useState(false);
  const [grantsFor, setGrantsFor] = useState<TeamRow | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<TeamRow | null>(null);
  // OCP-755 - role change / MFA reset, each with a reason
  const [reasonAction, setReasonAction] = useState<{ kind: 'role'; target: TeamRow; role: string } | { kind: 'mfa'; target: TeamRow } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api<{ items: TeamRow[] }>('/v1/admin/team');
      setItems(res.items);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Load failed (HTTP ${err.status}).`);
      } else {
        setError('Load failed.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // OCP-699: only humans keep the admin reachable - bots never count.
  const activeSuperAdminCount = useMemo(
    () =>
      (items ?? []).filter((u) => u.is_active && u.role === 'super_admin' && u.kind !== 'bot').length,
    [items],
  );

  async function reactivate(target: TeamRow) {
    try {
      await api(`/v1/admin/team/${target.id}/reactivate`, { method: 'PATCH' });
      await load();
    } catch {
      setError('Reactivate failed.');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Team</h1>
          <p className="mt-1 text-xs text-gray-500">
            Operators with access to admin.orboto.io. Invite a teammate or
            deactivate an old account.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCreatingBot(true)}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
            title="A bot is an agent's own operator identity: no password, no consent, authenticates with a grant (OCP-699)"
          >
            Create bot
          </button>
          <button
            type="button"
            onClick={() => setInviting(true)}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Invite operator
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && !items && <div className="text-sm text-gray-500">Loading…</div>}

      {items && items.length > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Active sessions</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {items.map((u) => {
              const isSelf = user?.email === u.email;
              const isLastActiveSuperAdmin =
                u.is_active && u.role === 'super_admin' && u.kind !== 'bot' && activeSuperAdminCount === 1;
              const deactivateBlockedReason = isSelf
                ? 'Cannot deactivate yourself.'
                : isLastActiveSuperAdmin
                  ? 'Cannot deactivate the last active super_admin - invite someone else first.'
                  : null;
              return (
                <tr key={u.id} className={!u.is_active ? 'opacity-60' : ''}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">
                      {u.email}
                      {u.kind === 'bot' && (
                        <span className="ml-1.5 rounded bg-violet-100 dark:bg-violet-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-violet-800 dark:text-violet-300" data-testid={`bot-badge-${u.email}`}>bot</span>
                      )}
                    </div>
                    {u.display_name && <div className="text-xs text-gray-600 dark:text-gray-400">{u.display_name}{u.purpose ? ` - ${u.purpose}` : ''}</div>}
                    {u.pending_setup && (
                      <div className="mt-0.5 text-[10px] uppercase text-amber-700 dark:text-amber-300">
                        pending setup
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {user?.role === 'super_admin' && !isSelf && u.is_active ? (
                      <select
                        value={u.role}
                        onChange={(e) => setReasonAction({ kind: 'role', target: u, role: e.target.value })}
                        title={ROLE_HELP[u.role]}
                        className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-1 py-0.5 text-xs"
                        data-testid={`role-${u.email}`}
                      >
                        {Object.keys(ROLE_HELP).map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : (
                      <span title={ROLE_HELP[u.role]}>{u.role}</span>
                    )}
                    {u.kind !== 'bot' && (
                      <div className={`mt-0.5 text-[10px] uppercase ${u.mfa_enabled ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`} data-testid={`mfa-${u.email}`}>
                        {u.mfa_enabled ? '2FA on' : '2FA off'}
                        {u.mfa_enabled && user?.role === 'super_admin' && !isSelf && (
                          <button type="button" onClick={() => setReasonAction({ kind: 'mfa', target: u })} className="ml-1.5 normal-case text-brand-500 hover:underline">reset</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {u.is_active ? (
                      <span className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs text-emerald-800 dark:text-emerald-300">
                        active
                      </span>
                    ) : (
                      <span className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400">
                        deactivated
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {u.kind === 'bot' ? (
                      <button type="button" className="text-brand-500 hover:underline" onClick={() => setGrantsFor(u)} title="Grants of this bot">
                        {u.active_grants} grant{u.active_grants === 1 ? '' : 's'}
                      </button>
                    ) : (
                      u.active_sessions
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {u.last_login_at
                      ? fmtDate(u.last_login_at)
                      : '-'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {u.is_active ? (
                      <button
                        type="button"
                        disabled={Boolean(deactivateBlockedReason)}
                        title={deactivateBlockedReason ?? 'Deactivate this operator'}
                        onClick={() => setConfirmDeactivate(u)}
                        className="text-xs text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void reactivate(u)}
                        className="text-xs text-brand-500 hover:underline"
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {reasonAction && (
        <ReasonDialog
          title={reasonAction.kind === 'role' ? `Change ${reasonAction.target.email} to ${reasonAction.role}` : `Reset the second factor of ${reasonAction.target.email}`}
          hint={reasonAction.kind === 'role' ? ROLE_HELP[reasonAction.role] ?? '' : 'Signs the account out everywhere (sessions and connected AI clients); it sets up a new authenticator at its next login. Say how you checked it is really them.'}
          onCancel={() => setReasonAction(null)}
          onSubmit={async (reason) => {
            const a = reasonAction;
            if (a.kind === 'role') await api(`/v1/admin/team/${a.target.id}/role`, { method: 'PATCH', body: { role: a.role, reason } });
            else await api(`/v1/admin/team/${a.target.id}/mfa/reset`, { method: 'POST', body: { reason } });
            setReasonAction(null);
            await load();
          }}
        />
      )}

      {creatingBot && (
        <CreateBotModal
          onCancel={() => setCreatingBot(false)}
          onDone={async () => {
            setCreatingBot(false);
            await load();
          }}
        />
      )}
      {grantsFor && (
        <BotGrantsModal
          bot={grantsFor}
          onClose={async () => {
            setGrantsFor(null);
            await load();
          }}
        />
      )}
      {inviting && (
        <InviteModal
          onCancel={() => setInviting(false)}
          onDone={async () => {
            setInviting(false);
            await load();
          }}
        />
      )}

      {confirmDeactivate && (
        <DeactivateModal
          target={confirmDeactivate}
          onCancel={() => setConfirmDeactivate(null)}
          onDone={async () => {
            setConfirmDeactivate(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/** OCP-699 - a bot account: email, display name, purpose; no password, no mail. */
function CreateBotModal({ onCancel, onDone }: { onCancel: () => void; onDone: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit() {
    if (!email.trim() || !displayName.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api('/v1/admin/team/bots', { method: 'POST', body: { email: email.trim().toLowerCase(), display_name: displayName.trim(), purpose: purpose.trim() || undefined } });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(body?.error === 'email_already_invited' ? 'That email already exists on the team.' : `Create failed (HTTP ${err.status}).`);
      } else {
        setError('Create failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl">
        <h2 className="mb-3 text-base font-semibold">Create bot account</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          A bot is an agent's own operator identity: no password, no setup mail, no consent screen. It authenticates with a grant you mint afterwards, so the audit log names the bot and not you.
        </p>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ocp-agent@orboto.io" required autoFocus aria-label="bot email" className="mb-2 w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" />
        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="display name, e.g. OCP agent" required aria-label="bot display name" className="mb-2 w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" />
        <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="purpose (optional)" aria-label="bot purpose" className="mb-3 w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" />
        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={submitting || !email.trim() || !displayName.trim()} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create bot'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** OCP-699 - the grants of one bot: list, mint (token shown once), revoke with reason. */
function BotGrantsModal({ bot, onClose }: { bot: TeamRow; onClose: () => Promise<void> }) {
  const [grants, setGrants] = useState<BotGrant[] | null>(null);
  const [clients, setClients] = useState<OAuthClientRow[]>([]);
  const [clientId, setClientId] = useState('');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [ttlDays, setTtlDays] = useState('365');
  const [minted, setMinted] = useState<{ token: string; grant: BotGrant } | null>(null);
  const [revoking, setRevoking] = useState<BotGrant | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGrants = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([
        api<{ items: BotGrant[] }>(`/v1/admin/team/bots/${bot.id}/grants`),
        api<{ items: OAuthClientRow[] }>('/v1/admin/oauth-clients'),
      ]);
      setGrants(g.items);
      setClients(c.items);
      setClientId((cur) => cur || c.items[0]?.client_id || '');
    } catch (err) {
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.');
    }
  }, [bot.id]);
  useEffect(() => {
    void loadGrants();
  }, [loadGrants]);

  async function mint() {
    if (!clientId || !label.trim() || reason.trim().length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ grant: BotGrant; token: string }>(`/v1/admin/team/bots/${bot.id}/grants`, {
        method: 'POST',
        body: { client_id: clientId, label: label.trim(), reason: reason.trim(), ttl_days: Number(ttlDays) || undefined },
      });
      setMinted(res);
      setLabel('');
      setReason('');
      await loadGrants();
    } catch (err) {
      setError(err instanceof ApiError ? `Mint failed (HTTP ${err.status}).` : 'Mint failed.');
    } finally {
      setBusy(false);
    }
  }
  async function revoke() {
    if (!revoking || revokeReason.trim().length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/team/bots/${bot.id}/grants/${revoking.id}`, { method: 'DELETE', body: { reason: revokeReason.trim() } });
      setRevoking(null);
      setRevokeReason('');
      await loadGrants();
    } catch (err) {
      setError(err instanceof ApiError ? `Revoke failed (HTTP ${err.status}).` : 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4" onClick={() => void onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl" data-testid="bot-grants">
        <h2 className="mb-1 text-base font-semibold">Grants of {bot.display_name ?? bot.email}</h2>
        <p className="mb-3 text-xs text-gray-500">A grant is a long-lived access token bound to an OAuth client. It is shown exactly once; the daemon presents it as a Bearer header (OCP_MCP_TOKEN). Rotate by minting a new one and revoking the old.</p>
        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
        {minted && (
          <div className="mb-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 p-3 text-xs" data-testid="minted-token">
            <div className="font-medium">Token for "{minted.grant.label}" - shown once, copy it now:</div>
            <code className="mt-1 block break-all select-all">{minted.token}</code>
          </div>
        )}
        <table className="w-full text-xs">
          <thead className="text-left uppercase text-gray-500">
            <tr>
              <th className="py-1">Label</th>
              <th>Client</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {(grants ?? []).map((g) => (
              <tr key={g.id} data-testid={`grant-${g.id}`}>
                <td className="py-1">{g.label}<div className="text-gray-500">{g.reason}</div></td>
                <td>{g.client_name ?? g.client_id}</td>
                <td>
                  <span className={g.status === 'active' ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500'}>{g.status}</span>
                  {g.revoked_at && <div className="text-gray-500">by {g.revoked_by}: {g.revoke_reason}</div>}
                </td>
                <td>{fmtDate(g.expires_at)}</td>
                <td>{g.last_used_at ? fmtDate(g.last_used_at) : 'never'}</td>
                <td className="text-right">
                  {g.status === 'active' && (
                    <button type="button" className="text-red-600 hover:underline" onClick={() => setRevoking(g)}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
            {grants && grants.length === 0 && (
              <tr><td colSpan={6} className="py-2 text-gray-500">No grant yet.</td></tr>
            )}
          </tbody>
        </table>
        {revoking ? (
          <div className="mt-3 rounded border border-red-300 p-3 text-xs">
            <div className="mb-2">Revoke "{revoking.label}"? The daemon using it gets 401 from the next request on.</div>
            <input type="text" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} placeholder="reason (required)" aria-label="revoke reason" className="mb-2 w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRevoking(null)} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
              <button type="button" onClick={() => void revoke()} disabled={busy || revokeReason.trim().length < 3} className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Revoke grant</button>
            </div>
          </div>
        ) : (
          bot.is_active && (
            <div className="mt-3 grid gap-2 md:grid-cols-2 text-xs">
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="oauth client" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm">
                {clients.length === 0 && <option value="">no OAuth client registered</option>}
                {clients.map((c) => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
              <input type="number" min={1} max={730} value={ttlDays} onChange={(e) => setTtlDays(e.target.value)} aria-label="ttl days" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" title="Validity in days (max 730)" />
              <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label, e.g. ocp-agent daemon on the z440" aria-label="grant label" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" />
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited)" aria-label="grant reason" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm" />
              <div className="md:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => void onClose()} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Close</button>
                <button type="button" onClick={() => void mint()} disabled={busy || !clientId || !label.trim() || reason.trim().length < 3} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
                  {busy ? 'Minting...' : 'Mint grant'}
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function InviteModal({
  onCancel,
  onDone,
}: {
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api('/v1/admin/team/invite', {
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (body?.error === 'email_already_invited') {
          setError('That email is already on the team. Reactivate the row instead.');
        } else {
          setError(`Invite failed (HTTP ${err.status}).`);
        }
      } else {
        setError('Invite failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Invite operator</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          They'll receive a magic-link email to set their password. The link
          is valid for 24 hours.
        </p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@orboto.io"
          required
          autoFocus
          className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-sm"
        />
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!email.trim() || submitting}
            onClick={() => void submit()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeactivateModal({
  target,
  onCancel,
  onDone,
}: {
  target: TeamRow;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = typed.trim() === target.email;

  async function submit() {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/team/${target.id}/deactivate`, { method: 'PATCH' });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (body?.error === 'last_super_admin_protection') {
          setError('Cannot deactivate the last active super_admin.');
        } else if (body?.error === 'cannot_deactivate_self') {
          setError('Cannot deactivate yourself.');
        } else {
          setError(`HTTP ${err.status}`);
        }
      } else {
        setError('Deactivate failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Deactivate operator</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          This revokes every active session, every MCP / OAuth connection
          (access tokens, refresh tokens, pending authorization codes) and bot
          grant, removes the operator's push notification devices and prevents
          the operator from logging back in until you reactivate. After a
          reactivation the operator connects each MCP client again through the
          consent screen and enables push notifications again. Type{' '}
          <code className="font-mono text-xs">{target.email}</code> to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={target.email}
          className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
        />
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || submitting}
            onClick={() => void submit()}
            className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** OCP-755 - a required, audited reason for a role change or an MFA reset. */
function ReasonDialog({ title, hint, onCancel, onSubmit }: { title: string; hint: string; onCancel: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4" onClick={onCancel}>
      <div className="w-full max-w-md space-y-3 rounded-lg bg-white dark:bg-gray-900 p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-sm font-semibold">{title}</h2>
        {hint && <p className="text-xs text-gray-600 dark:text-gray-400">{hint}</p>}
        <label className="block text-xs text-gray-600 dark:text-gray-400">
          Reason (audited)
          <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm" data-testid="reason-input" />
        </label>
        {err && <p className="text-xs text-red-700 dark:text-red-400">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs">Cancel</button>
          <button
            type="button"
            disabled={busy || reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await onSubmit(reason.trim());
              } catch (e) {
                const b = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
                setErr(b?.error === 'last_super_admin_protection' ? 'This is the last active super_admin - promote someone else first.' : b?.error ?? 'Request failed.');
              } finally {
                setBusy(false);
              }
            }}
            className="rounded bg-brand-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
            data-testid="reason-submit"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
